"use client";

import { Download, Paperclip } from "lucide-react";

import LomdaUploadFile from "../shared/lomdaUploadFile";
import { Input } from "@/components/ui/input";
import { t } from "@/lib/localization";
import { AttachmentBlock as AttachmentBlockType } from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../../store/useLomdaV3Editor";
import { useBlockEditable } from "../../../hooks/useBlockEditable";
import {
    LOMDA_DROPZONE_CLASS,
    LOMDA_DROPZONE_DRAG_CLASS,
} from "../shared/dropzone";
import { DOCUMENT_UPLOAD } from "../../../lib/uploadLimits";

function formatSize(bytes: number): string {
    if (!bytes) return "";
    const mb = bytes / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export default function AttachmentBlock({
    block,
}: {
    block: AttachmentBlockType;
}) {
    const updateBlock = useLomdaV3Editor((s) => s.updateBlock);

    const { url, name, size } = block.data;
    const isEditable = useBlockEditable();

    if (!url) {
        if (!isEditable) return null;
        return (
            <LomdaUploadFile
                reuse="file"
                reuseLayout="canvas"
                options={{
                    ...DOCUMENT_UPLOAD,
                    className:
                        `${LOMDA_DROPZONE_CLASS} h-32 w-full rounded-xl`,
                    dragClassName: LOMDA_DROPZONE_DRAG_CLASS,
                    title: {
                        text: t("lomda-v3-editor.block.attachment.upload-title"),
                        keyName: "lomda-v3-editor.block.attachment.upload-title",
                    },
                    subtitle: {
                        text: t(
                            "lomda-v3-editor.block.attachment.upload-subtitle"
                        ),
                        keyName:
                            "lomda-v3-editor.block.attachment.upload-subtitle",
                    },
                }}
                onUploadFinished={(file) =>
                    updateBlock(block.id, {
                        url: file.fileUrl,
                        name: file.fileName,
                        size: file.fileSize ?? 0,
                    })
                }
            />
        );
    }

    return (
        <div className="flex flex-col gap-2">
            {isEditable ? (
                <div className="lomda-v3-card-surface flex items-center gap-3 rounded-xl border p-3">
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                        <Paperclip className="size-5" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col text-start">
                        <Input
                            value={name}
                            onChange={(e) =>
                                updateBlock(block.id, { name: e.target.value })
                            }
                            placeholder={t(
                                "lomda-v3-editor.block.attachment.name"
                            )}
                            className="lomda-v3-page-text h-7 border-transparent px-1 lomda-v3-text-small shadow-none focus-visible:border-input"
                        />
                        {size > 0 && (
                            <span className="px-1 lomda-v3-text-small text-muted-foreground">
                                {formatSize(size)}
                            </span>
                        )}
                    </span>
                </div>
            ) : (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={name}
                    className="lomda-v3-card-text flex items-center gap-3 rounded-xl border bg-card p-3 transition hover:border-primary/50 hover:bg-muted/30"
                >
                    <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                        <Paperclip className="size-5" />
                    </span>
                    <span className="flex min-w-0 flex-1 flex-col text-start">
                        <span className="truncate lomda-v3-text-small">
                            {name}
                        </span>
                        {size > 0 && (
                            <span className="lomda-v3-text-small text-muted-foreground">
                                {formatSize(size)}
                            </span>
                        )}
                    </span>
                    <Download className="size-4 shrink-0 text-muted-foreground" />
                </a>
            )}
        </div>
    );
}
